/*
 * BundleCartSession
 * -----------------
 * Isolates the lp_2 "build a bundle" purchase as its own cart.
 *
 * Flow:
 *  - finalize(): snapshot the current cart -> ADD the bundle items (tagged with
 *    a `_bundle` line property) -> REMOVE the original lines -> mark a session.
 *    The cart now holds only the bundle, ready to check out on its own.
 *  - On the cart drawer closing without heading to checkout (abandon), or on a
 *    later page load after the bundle was purchased, the original cart is
 *    restored (re-add the snapshot, remove the bundle lines, replay note/attrs).
 *
 * Guardrails:
 *  - ADD-BEFORE-REMOVE everywhere, so a failed call never leaves the cart empty.
 *  - The snapshot is written to sessionStorage AND read back before any cart
 *    mutation; if storage is unavailable we fall back to a plain add (no
 *    isolation) rather than mutate a cart we cannot restore.
 *  - Phase-aware rollback on failure leaves the shopper's cart intact.
 *  - note + cart attributes (where the tiered discount lives) and per-line
 *    properties / selling_plan are preserved across the round trip.
 *
 * ACCEPTED LIMITATIONS (by product decision):
 *  - The cart is one shared server-side cart, so isolating in one tab changes
 *    what the shopper sees in their other tabs (which have no snapshot).
 *  - Checkout is the off-theme Gokwik flow, so "restore after purchase" is
 *    best-effort, inferred from cart state on return, and only works when the
 *    shopper comes back in the SAME tab (sessionStorage scope). It can miss, or
 *    occasionally fire on an unrelated empty cart. The flag is always cleared
 *    after one restore attempt to bound any misfire.
 */
(function () {
  'use strict';

  if (window.BundleCartSession) return;

  var SNAPSHOT_KEY = 'bundleIso:snapshot';
  var SESSION_KEY = 'bundleIso:session';
  var INTENT_RESET_MS = 4000;

  var restoring = false;

  /* ---------------- sessionStorage helpers (fully guarded) ---------------- */
  function store() {
    try {
      return window.sessionStorage;
    } catch (e) {
      return null;
    }
  }
  function readJSON(key) {
    var s = store();
    if (!s) return null;
    try {
      var raw = s.getItem(key);
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  }
  function writeJSON(key, obj) {
    var s = store();
    if (!s) return false;
    try {
      s.setItem(key, JSON.stringify(obj));
      return s.getItem(key) != null; // read back to verify
    } catch (e) {
      return false;
    }
  }
  function removeKey(key) {
    var s = store();
    if (!s) return;
    try {
      s.removeItem(key);
    } catch (e) {
      /* ignore */
    }
  }
  function clearKeys() {
    removeKey(SNAPSHOT_KEY);
    removeKey(SESSION_KEY);
  }
  function readSession() {
    return readJSON(SESSION_KEY);
  }
  function readSnapshot() {
    return readJSON(SNAPSHOT_KEY);
  }

  /* ---------------- routes (locale-aware via window.routes) ---------------- */
  function routes() {
    return window.routes || {};
  }
  function cartUrl() {
    return routes().cart_url || '/cart';
  }
  function cartJsUrl() {
    return cartUrl() + '.js';
  }
  function addUrl() {
    return routes().cart_add_url || '/cart/add.js';
  }
  function updateUrl() {
    return routes().cart_update_url || '/cart/update.js';
  }

  /* ---------------- fetch helpers ---------------- */
  function postCart(url, body) {
    var config =
      typeof fetchConfig === 'function'
        ? fetchConfig('javascript')
        : { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' } };
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    config.body = JSON.stringify(body);
    return fetch(url, config)
      .then(function (r) {
        return r.json();
      })
      .then(function (json) {
        if (json && json.status) {
          // Shopify error shape: { status, message, description }
          var err = new Error(json.description || json.message || 'Cart request failed');
          err.cartError = json;
          throw err;
        }
        return json;
      });
  }
  function getCart() {
    return fetch(cartJsUrl()).then(function (r) {
      return r.json();
    });
  }

  /* ---------------- drawer / sections / pubsub ---------------- */
  function cartEl() {
    return document.querySelector('cart-notification') || document.querySelector('cart-drawer');
  }
  function drawerEl() {
    return document.querySelector('cart-drawer');
  }
  function sectionIds() {
    var c = cartEl();
    if (c && typeof c.getSectionsToRender === 'function') {
      return c.getSectionsToRender().map(function (s) {
        return s.id;
      });
    }
    return null;
  }
  function withSections(body) {
    var ids = sectionIds();
    if (ids) {
      body.sections = ids;
      body.sections_url = window.location.pathname;
    }
    return body;
  }
  function publishUpdate(cartData) {
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'bundle-isolation', cartData: cartData });
    }
  }

  /* Re-render the cart sections. `reopen` controls whether the drawer opens. */
  function renderCart(response, reopen) {
    var drawer = drawerEl();
    if (response && response.sections && drawer && typeof drawer.getSectionsToRender === 'function') {
      if (reopen && typeof drawer.renderContents === 'function') {
        drawer.renderContents(response); // swaps sections + re-opens the drawer
        return;
      }
      // Swap sections WITHOUT opening the drawer (mirrors cart-drawer.getSectionInnerHTML).
      drawer.getSectionsToRender().forEach(function (section) {
        var el = section.selector
          ? document.querySelector(section.selector)
          : document.getElementById(section.id);
        var html = response.sections[section.id];
        if (!el || !html) return;
        try {
          el.innerHTML = new DOMParser()
            .parseFromString(html, 'text/html')
            .querySelector(section.selector || '.shopify-section').innerHTML;
        } catch (e) {
          /* ignore a single section render failure */
        }
      });
    }
  }

  /* ---------------- snapshot building ---------------- */
  function buildSnapshot(cart) {
    var items = [];
    (cart.items || []).forEach(function (it) {
      if (typeof it.variant_id !== 'number') return; // skip gift cards / non-addable lines
      var entry = { id: it.variant_id, quantity: it.quantity };
      if (it.properties && Object.keys(it.properties).length) entry.properties = it.properties;
      if (it.selling_plan_allocation && it.selling_plan_allocation.selling_plan) {
        entry.selling_plan = it.selling_plan_allocation.selling_plan.id;
      }
      items.push(entry);
    });
    return {
      version: 1,
      token: cart.token || null,
      savedAt: Date.now(),
      note: typeof cart.note === 'string' ? cart.note : null,
      attributes: cart.attributes || {},
      items: items,
    };
  }

  function snapshotToAddItems(snapshot) {
    return (snapshot.items || []).map(function (it) {
      var o = { id: it.id, quantity: it.quantity };
      if (it.properties) o.properties = it.properties;
      if (it.selling_plan) o.selling_plan = it.selling_plan;
      return o;
    });
  }

  /* ---------------- low-level cart ops ---------------- */
  function addItems(items, wantSections) {
    var body = { items: items };
    return postCart(addUrl(), wantSections ? withSections(body) : body);
  }
  function zeroOutKeys(keys, wantSections) {
    if (!keys || !keys.length) return Promise.resolve(null);
    var updates = {};
    keys.forEach(function (k) {
      updates[k] = 0;
    });
    var body = { updates: updates };
    return postCart(updateUrl(), wantSections ? withSections(body) : body);
  }
  function removeBundleLines(wantSections) {
    return getCart().then(function (cart) {
      var keys = (cart.items || [])
        .filter(function (it) {
          return it.properties && it.properties._bundle;
        })
        .map(function (it) {
          return it.key;
        });
      return zeroOutKeys(keys, wantSections);
    });
  }
  function restoreNoteAttributes(snapshot, wantSections) {
    var body = {};
    if (snapshot.note != null) body.note = snapshot.note;
    if (snapshot.attributes && Object.keys(snapshot.attributes).length) body.attributes = snapshot.attributes;
    if (!Object.keys(body).length) return Promise.resolve(null);
    return postCart(updateUrl(), wantSections ? withSections(body) : body);
  }

  // FAST restore: one /cart/update.js that removes the bundle lines AND restores
  // note + attributes (and re-renders) — collapses 3 calls into 1.
  function removeBundleWithLevel(snapshot, keys, wantSections) {
    var body = {};
    if (keys && keys.length) {
      var updates = {};
      keys.forEach(function (k) {
        updates[k] = 0;
      });
      body.updates = updates;
    }
    if (snapshot && snapshot.note != null) body.note = snapshot.note;
    if (snapshot && snapshot.attributes && Object.keys(snapshot.attributes).length) {
      body.attributes = snapshot.attributes;
    }
    if (!Object.keys(body).length) return Promise.resolve(null);
    return postCart(updateUrl(), wantSections ? withSections(body) : body);
  }

  // Bundle line keys: prefer the ones captured at finalize (no network); fall
  // back to a /cart.js lookup for sessions created before this was stored.
  function getBundleKeys() {
    var s = readSession();
    if (s && s.bundleKeys && s.bundleKeys.length) return Promise.resolve(s.bundleKeys.slice());
    return getCart().then(function (cart) {
      return (cart.items || [])
        .filter(function (it) {
          return it.properties && it.properties._bundle;
        })
        .map(function (it) {
          return it.key;
        });
    });
  }

  /* ---------------- FINALIZE ---------------- */
  // items: [{ id, quantity }]
  // The bundle is ADDED to the shopper's EXISTING cart — the current cart is NOT
  // removed, replaced, snapshotted, or restored. Items keep the hidden `_bundle`
  // property so the cart drawer / discount logic can still recognise them.
  function finalize(opts) {
    opts = opts || {};
    var bundleItems = (opts.items || []).map(function (it) {
      return { id: it.id, quantity: it.quantity || 1, properties: { _bundle: '1' } };
    });
    if (!bundleItems.length) return Promise.resolve({ ok: false });

    return plainAdd(bundleItems).catch(function (err) {
      console.error('BundleCartSession.finalize (add to existing cart) failed:', err);
      return { ok: false, error: err };
    });
  }

  // Plain add (no isolation) — used when sessionStorage is unavailable.
  function plainAdd(items) {
    return addItems(items, true).then(function (resp) {
      renderCart(resp, true);
      publishUpdate(resp);
      clearKeys();
      return { ok: true, isolated: false };
    });
  }

  // Bring the cart back to the original after a failed finalize.
  function revert(phase, snapshot) {
    var done = function () {
      clearKeys();
    };
    if (phase === 'snapshot') {
      // Add failed: cart untouched.
      done();
      return Promise.resolve();
    }
    if (phase === 'added') {
      // Bundle added but originals NOT removed: just drop the bundle lines.
      return removeBundleLines(true)
        .then(function (resp) {
          renderCart(resp, false);
          publishUpdate(resp);
        })
        .catch(function () {})
        .then(done);
    }
    // phase === 'removed': originals gone, bundle present -> full restore.
    if (!snapshot) {
      done();
      return Promise.resolve();
    }
    var addList = snapshotToAddItems(snapshot);
    return getBundleKeys()
      .then(function (keys) {
        var addStep = addList.length ? addItems(addList, false) : Promise.resolve(null);
        return addStep.then(function () {
          return removeBundleWithLevel(snapshot, keys, true);
        });
      })
      .then(function (resp) {
        renderCart(resp, false);
        publishUpdate(resp);
      })
      .catch(function () {})
      .then(done);
  }

  /* ---------------- RESTORE (abandon + after-purchase) ---------------- */
  function restoreOriginal(opts) {
    opts = opts || {};
    if (restoring) return Promise.resolve();
    var snapshot = readSnapshot();
    if (!snapshot) {
      clearKeys();
      return Promise.resolve();
    }
    restoring = true;
    // Optimized abandon restore: 2 round-trips instead of 4.
    //  1) re-add the originals (preserves properties / selling_plan), then
    //  2) ONE /cart/update.js that removes the bundle lines + restores
    //     note/attributes + returns the rendered sections.
    // Bundle line keys come from the session (captured at finalize), so there is
    // no extra /cart.js read in the common case.
    var addList = snapshotToAddItems(snapshot);
    return getBundleKeys()
      .then(function (keys) {
        var addStep = addList.length ? addItems(addList, false) : Promise.resolve(null);
        return addStep.then(function () {
          return removeBundleWithLevel(snapshot, keys, true);
        });
      })
      .then(function (resp) {
        renderCart(resp, !!opts.reopen);
        publishUpdate(resp);
      })
      .catch(function (e) {
        console.error('BundleCartSession.restoreOriginal failed:', e);
      })
      .then(function () {
        clearKeys();
        restoring = false;
      });
  }

  /* ---------------- checkout-intent latch ---------------- */
  function setCheckoutIntent(value) {
    var s = readSession();
    if (!s) return;
    s.checkoutIntent = !!value;
    writeJSON(SESSION_KEY, s);
    if (value) {
      // If checkout is blocked and the shopper abandons instead, don't leave the
      // latch stuck — clear it after a short delay if the page hasn't navigated.
      setTimeout(function () {
        var cur = readSession();
        if (cur && cur.active && cur.checkoutIntent) {
          cur.checkoutIntent = false;
          writeJSON(SESSION_KEY, cur);
        }
      }, INTENT_RESET_MS);
    }
  }

  function isActive() {
    var s = readSession();
    return !!(s && s.active);
  }

  /* ---------------- after-purchase check (best-effort) ---------------- */
  function afterReturnCheck() {
    var s = readSession();
    if (!s || !s.active) return;
    getCart()
      .then(function (cart) {
        var hasBundle = (cart.items || []).some(function (it) {
          return (
            (it.properties && it.properties._bundle === '1') ||
            (s.bundleVariantIds || []).indexOf(it.variant_id) !== -1
          );
        });
        var gone =
          cart.item_count === 0 ||
          (cart.token && s.bundleToken && cart.token !== s.bundleToken) ||
          !hasBundle;
        if (gone) restoreOriginal({ reopen: false });
      })
      .catch(function () {});
  }

  /* ---------------- self-wired listeners ---------------- */
  // Abandon: the drawer closed and we're not heading to checkout -> restore.
  document.addEventListener('cart-drawer:close', function () {
    var s = readSession();
    if (!s || !s.active || s.checkoutIntent) return;
    restoreOriginal({ reopen: false });
  });

  // Record checkout intent so an immediate close doesn't undo the bundle.
  document.addEventListener(
    'click',
    function (e) {
      if (!e.target || typeof e.target.closest !== 'function') return;
      if (
        e.target.closest('.gokwik-checkout button') ||
        e.target.closest('button[name="checkout"]') ||
        e.target.closest('.cart-drawer__footer-checkout-button')
      ) {
        setCheckoutIntent(true);
      }
    },
    true
  );

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', afterReturnCheck);
  } else {
    afterReturnCheck();
  }

  /* ===================================================================
   * Bundle quantity lock-step.
   * While a bundle is in the cart (lines tagged with the hidden `_bundle`
   * property), the bundle is bought as a SET: changing the quantity of one
   * bundle line changes every bundle line to the same quantity. We patch
   * Dawn's CartItems.updateQuantity (inherited by <cart-drawer-items>) so a
   * change to a bundle line becomes a single /cart/update.js that sets all
   * bundle lines, then re-renders the cart sections the way Dawn does.
   *
   * The patch is a no-op unless a bundle session is active (isActive()), so
   * ordinary carts keep Dawn's original single-line behavior with no overhead.
   * Setting the quantity to 0 (remove) clears the whole bundle.
   * =================================================================== */
  function cartJsUrl() {
    return ((window.routes && window.routes.cart_url) || '/cart') + '.js';
  }

  function renderCartSections(itemsEl, parsed, variantId) {
    itemsEl.classList.toggle('is-empty', parsed.item_count === 0);
    var drawer = document.querySelector('cart-drawer');
    var footer = document.getElementById('main-cart-footer');
    if (footer) footer.classList.toggle('is-empty', parsed.item_count === 0);
    if (drawer) drawer.classList.toggle('is-empty', parsed.item_count === 0);

    itemsEl.getSectionsToRender().forEach(function (section) {
      var container = document.getElementById(section.id);
      if (!container) return;
      var target = container.querySelector(section.selector) || container;
      if (parsed.sections && parsed.sections[section.section] != null) {
        target.innerHTML = itemsEl.getSectionInnerHTML(parsed.sections[section.section], section.selector);
      }
    });

    if (typeof CartOperationLock !== 'undefined') CartOperationLock.syncCleanup();

    if (itemsEl.tagName === 'CART-DRAWER-ITEMS') {
      setTimeout(function () {
        if (window.claimMinisInstance) window.claimMinisInstance.fetchCartAndUpdateDisplay();
      }, 150);
    }

    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: parsed, variantId: variantId });
    }
  }

  function lockstepBundleQuantity(itemsEl, line, quantity, variantId, bundleKeys) {
    if (typeof itemsEl.enableLoading === 'function') itemsEl.enableLoading(line);
    if (typeof CartOperationLock !== 'undefined') CartOperationLock.lock();

    var qty = parseInt(quantity, 10);
    if (isNaN(qty) || qty < 0) qty = 1;

    var updates = {};
    bundleKeys.forEach(function (k) {
      updates[k] = qty;
    });

    var config =
      typeof fetchConfig === 'function'
        ? fetchConfig()
        : { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' } };
    config.body = JSON.stringify({
      updates: updates,
      sections: itemsEl.getSectionsToRender().map(function (s) {
        return s.section;
      }),
      sections_url: window.location.pathname,
    });

    var url = (window.routes && window.routes.cart_update_url) || '/cart/update.js';
    fetch(url, config)
      .then(function (r) {
        return r.text();
      })
      .then(function (state) {
        renderCartSections(itemsEl, JSON.parse(state), variantId);
        if (typeof CartOperationLock !== 'undefined') CartOperationLock.unlock();
      })
      .catch(function () {
        var errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
        if (errors && window.cartStrings) errors.textContent = window.cartStrings.error;
        if (typeof CartOperationLock !== 'undefined') CartOperationLock.unlock();
      })
      .finally(function () {
        if (typeof itemsEl.disableLoading === 'function') itemsEl.disableLoading(line);
      });
  }

  function patchBundleQuantityLockstep(Ctor) {
    if (!Ctor || Ctor.prototype.__bundleQtyPatched) return;
    Ctor.prototype.__bundleQtyPatched = true;

    var original = Ctor.prototype.updateQuantity;

    Ctor.prototype.updateQuantity = function (line, quantity, name, variantId) {
      var self = this;

      // Fast path: no active bundle -> Dawn's normal single-line update.
      if (!isActive()) {
        return original.call(self, line, quantity, name, variantId);
      }

      fetch(cartJsUrl(), { headers: { Accept: 'application/json' } })
        .then(function (r) {
          return r.json();
        })
        .then(function (cart) {
          var items = (cart && cart.items) || [];
          var changed = items[line - 1];
          var isBundleLine = !!(changed && changed.properties && changed.properties._bundle);
          var bundleKeys = items
            .filter(function (it) {
              return it.properties && it.properties._bundle;
            })
            .map(function (it) {
              return it.key;
            });

          // Only lock-step a bundle line when there is more than one bundle line.
          if (!isBundleLine || bundleKeys.length <= 1) {
            original.call(self, line, quantity, name, variantId);
            return;
          }
          lockstepBundleQuantity(self, line, quantity, variantId, bundleKeys);
        })
        .catch(function () {
          original.call(self, line, quantity, name, variantId);
        });
    };
  }

  if (window.customElements && customElements.whenDefined) {
    customElements.whenDefined('cart-items').then(function () {
      patchBundleQuantityLockstep(customElements.get('cart-items'));
    });
  }

  window.BundleCartSession = {
    finalize: finalize,
    restoreOriginal: restoreOriginal,
    setCheckoutIntent: setCheckoutIntent,
    isActive: isActive,
  };
})();
